import {useLocale, useMessages} from "i18n";
import formatBytes from "../../core/format/byteFormat";
import {Mode, Plan} from "../gating";
import {resolveUsage} from "../usageMeter";
import {MeterFill, MeterTrack, Remaining, Unlimited, UsageRow} from "./UsageMeter.styles";

interface Props {
	mode: Mode;
	plan: Plan;
	totalBytes: number;
	fileCount: number;
}

/**
 * Per-mode usage line (design `.usage` + `.meter`): the heat-gradient cap meter for "upload"
 * (green→amber→red as the transfer fills the plan allowance), and a file-count + "∞ Unlimited"
 * line for "instant". Sans typography throughout (the mono override was dropped in ui#49).
 */
export function UsageMeter({mode, plan, totalBytes, fileCount}: Props) {
	const {sender} = useMessages();
	const {locale} = useLocale();
	// Quota derivation (which plan allowance applies, and whether it applies at all) is a gating
	// rule, not layout — it lives in `home/usageMeter.ts` (ui#220). This view only picks the shape.
	const usage = resolveUsage(mode, plan, totalBytes);
	if (usage.kind === "unlimited") {
		return (
			<UsageRow>
				<span>{sender.filesCount(fileCount)} · {formatBytes(totalBytes, locale, 1)}</span>
				<Unlimited>{sender.unlimited}</Unlimited>
			</UsageRow>
		);
	}
	const {capBytes, remainingBytes, pct} = usage;
	const bg = pct > 0 ? Math.round(10000 / pct) : 100;
	return (
		<div>
			<UsageRow>
				<span><Remaining>{formatBytes(remainingBytes, locale, 1)}</Remaining> {sender.remaining}</span>
				{/* Same SI formatter as `remaining` (ui#95) — two unit systems on one line is the bug. */}
				<span>{sender.usageOf(formatBytes(totalBytes, locale, 1), formatBytes(capBytes, locale, 0))}</span>
			</UsageRow>
			<MeterTrack>
				<MeterFill $pct={pct} $bg={bg}/>
			</MeterTrack>
		</div>
	);
}
